import { useLocation, useRouter } from '@tanstack/react-router'
import { useDocChatStorageOwner } from 'features/knowledgeDocumentViewer/hooks/useDocChatStorageOwner'
import { useRenderedDocChatStorageOwner } from 'features/knowledgeDocumentViewer/hooks/useRenderedDocChatStorageOwner'
import type {
  DocChatConversation,
  DocumentTab,
} from 'features/knowledgeDocumentViewer/types'
import { applyStoredComposerDraftsToOpenTabs } from 'features/knowledgeDocumentViewer/utils/agenticChatComposerDraft'
import {
  createAgenticConversationTab,
  createNewAgenticChatTab,
} from 'features/knowledgeDocumentViewer/utils/agenticChatTabFactories'
import { getAgenticConversationId } from 'features/knowledgeDocumentViewer/utils/agenticConversationIdentity'
import type { DocChatPreview } from 'features/knowledgeDocumentViewer/utils/docChatPreview'
import {
  getPersistedTabState,
  persistTabState,
} from 'features/knowledgeDocumentViewer/utils/documentPageSessionStorage'
import { createWorkspaceHomeTab } from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import { useSyncActiveDestinationHref } from 'features/workspaceTabs/hooks/useSyncActiveDestinationHref'
import { useSyncBrowserDocumentTitle } from 'features/workspaceTabs/hooks/useSyncBrowserDocumentTitle'
import { useSyncDocChatStreamUpdates } from 'features/workspaceTabs/hooks/useSyncDocChatStreamUpdates'
import { useWorkspaceTabNavigation } from 'features/workspaceTabs/hooks/useWorkspaceTabNavigation'
import { useWorkspaceTabPersistence } from 'features/workspaceTabs/hooks/useWorkspaceTabPersistence'
import { clearWorkspaceTabChartState } from 'features/workspaceTabs/utils/clearWorkspaceTabChartState'
import { findReuseTargetTab } from 'features/workspaceTabs/utils/findReuseTargetTab'
import { navigateToWorkspaceTab } from 'features/workspaceTabs/utils/navigateToWorkspaceTab'
import {
  createResourceTab,
  createWorkspaceDocumentTab,
  type DestinationReuseMode,
  documentResourceKey,
  type OpenDocumentTabOptions,
  type OpenResourceTabOptions,
  STABLE_IDENTITY_DESTINATIONS,
} from 'features/workspaceTabs/utils/resourceTabs'
import { scheduleAfterPaint } from 'features/workspaceTabs/utils/scheduleAfterPaint'
import {
  createTabForDestination,
  type NavigateActiveTabOptions,
} from 'features/workspaceTabs/utils/targetTabs'
import {
  isWorkspaceScopeForSite,
  workspaceScope,
  workspaceScopeMode,
} from 'features/workspaceTabs/utils/workspaceScope'
import React, {
  createContext,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'

interface OpenDestinationTabOptions {
  destinationKey: string
  label: string
  href: string
  /**
   * Only honored for STABLE_IDENTITY_DESTINATIONS. Sidebar clicks use
   * 'focus-preserving-state' (the default); explicit cross-links use
   * 'focus-and-navigate'. Other destinations keep their legacy matching.
   */
  reuseMode?: DestinationReuseMode
}

/**
 * Leave/close guard for the active tab's route. Consulted before any tab-state
 * commit so a cancelled navigation leaves both the tab bar and the rendered
 * route untouched. Tabs that never register a guard are unaffected.
 */
export interface WorkspaceNavigationGuard {
  message: string
  isDirty: () => boolean
}

/**
 * Loading-aware validator for restored tabs. `isLoading` must be true until
 * permissions, navbar configuration, and release configuration have resolved;
 * tabs are only removed on definitive denial, never while loading.
 */
export interface WorkspaceTabAccessPolicy {
  isLoading: boolean
  canOpenTab: (tab: DocumentTab) => boolean
}

interface OpenAgenticConversationOptions {
  composerDraft?: string
  conversation: DocChatConversation
  preview?: DocChatPreview
  siteId: string
  title: string
}

interface WorkspaceTabsState {
  openTabs: DocumentTab[]
  activeTabId: string
}

interface WorkspaceTabsActions {
  setOpenTabs: Dispatch<SetStateAction<DocumentTab[]>>
  setActiveTabId: Dispatch<SetStateAction<string>>
  /** Swaps to another tab set, so only this site's own active/preview buckets
   * are honored; any other scope describes tab state this provider doesn't own. */
  setWorkspaceScope: (scope: string) => void
  openDestinationTab: (options: OpenDestinationTabOptions) => void
  openAgenticConversation: (options: OpenAgenticConversationOptions) => void
  /** Opens an empty chat, or one already asking `question`. */
  openNewAgenticChat: (options?: { question?: string }) => void
  openResourceTab: (options: OpenResourceTabOptions) => void
  openDocumentTab: (options: OpenDocumentTabOptions) => void
  /**
   * Like typing a URL into a browser tab: the active tab, chat included,
   * becomes the target. A target another tab already owns focuses that tab
   * instead.
   */
  navigateActiveTab: (options: NavigateActiveTabOptions) => void
  /**
   * Like Cmd/Ctrl+click in a browser: always a new tab, never reuse of one
   * already showing the target.
   */
  openNewTab: (options: NavigateActiveTabOptions) => void
  setActiveTabTitle: (title: string | null) => void
  /** Page-published unsaved-edits marker; reuse skips dirty tabs. */
  setActiveTabDirty: (dirty: boolean) => void
  activateWorkspaceTab: (tabId: string) => void
  closeWorkspaceTab: (tabId: string) => boolean
  registerNavigationGuard: (guard: WorkspaceNavigationGuard) => () => void
  /** Consult the active tab's leave guard before an external navigation the
   * provider cannot veto itself (site switches, preview flips). */
  confirmLeaveActiveTab: () => boolean
}

export type WorkspaceTabsContextValue = WorkspaceTabsState &
  WorkspaceTabsActions

const WorkspaceTabsStateContext = createContext<WorkspaceTabsState | null>(null)
const WorkspaceTabsActionsContext = createContext<WorkspaceTabsActions | null>(
  null
)

const getInitialState = (scope: string) => {
  const persisted = getPersistedTabState(scope)
  if (persisted) {
    return {
      openTabs: applyStoredComposerDraftsToOpenTabs(persisted.openTabs),
      activeTabId: persisted.activeTabId,
    }
  }

  const defaultTab = createWorkspaceHomeTab()
  return { openTabs: [defaultTab], activeTabId: defaultTab.id }
}

type WorkspaceTabsProviderProps = {
  siteId: string
  accessPolicy?: WorkspaceTabAccessPolicy
  children: React.ReactNode
}

export function WorkspaceTabsProvider(props: WorkspaceTabsProviderProps) {
  const storageOwner = useDocChatStorageOwner()
  return <UserWorkspaceTabsProvider key={storageOwner} {...props} />
}

function UserWorkspaceTabsProvider({
  siteId,
  accessPolicy,
  children,
}: WorkspaceTabsProviderProps) {
  const router = useRouter()
  const location = useLocation()
  const storageOwnerRef = useRenderedDocChatStorageOwner()
  const initialScope = workspaceScope(siteId, 'active')
  const [scope, setScope] = useState(initialScope)
  const [initialState] = useState(() => getInitialState(initialScope))
  const [openTabs, setOpenTabs] = useState(initialState.openTabs)
  const [activeTabId, setActiveTabId] = useState(initialState.activeTabId)
  useSyncDocChatStreamUpdates({ siteId, setOpenTabs })
  const scopeRef = useRef(scope)
  const openTabsRef = useRef(openTabs)
  const activeTabIdRef = useRef(activeTabId)
  const navigationGuardsRef = useRef(new Set<WorkspaceNavigationGuard>())

  scopeRef.current = scope
  openTabsRef.current = openTabs
  activeTabIdRef.current = activeTabId

  const registerNavigationGuard = useCallback(
    (guard: WorkspaceNavigationGuard) => {
      navigationGuardsRef.current.add(guard)
      return () => {
        navigationGuardsRef.current.delete(guard)
      }
    },
    []
  )

  /**
   * Runs before any tab-state commit that would unmount the active route.
   * Cancelling leaves the tab bar and the rendered route exactly as they were.
   */
  const confirmLeaveActiveTab = useCallback(() => {
    const guard = [...navigationGuardsRef.current].find((candidate) =>
      candidate.isDirty()
    )
    if (!guard) return true
    return window.confirm(guard.message)
  }, [])

  useSyncActiveDestinationHref({
    locationHref: location.href,
    setOpenTabs,
    activeTabIdRef,
  })

  // Site swaps keep preview/active mode; setWorkspaceScope owns mode changes.
  // A site change cannot be vetoed here (the outer site state has already
  // switched); initiators consult confirmLeaveActiveTab before switching.
  useEffect(() => {
    const nextScope = workspaceScope(
      siteId,
      workspaceScopeMode(scopeRef.current)
    )
    if (scopeRef.current === nextScope) return

    persistTabState(
      storageOwnerRef.current,
      scopeRef.current,
      openTabsRef.current,
      activeTabIdRef.current
    )
    const nextState = getInitialState(nextScope)
    scopeRef.current = nextScope
    setScope(nextScope)
    setOpenTabs(nextState.openTabs)
    setActiveTabId(nextState.activeTabId)
  }, [siteId, storageOwnerRef])

  useWorkspaceTabPersistence({
    storageOwnerRef,
    scope,
    openTabs,
    activeTabId,
    scopeRef,
    openTabsRef,
    activeTabIdRef,
  })

  useSyncBrowserDocumentTitle(location.href, activeTabId)

  const setWorkspaceScope = useCallback(
    (nextScope: string) => {
      if (!isWorkspaceScopeForSite(nextScope, siteId)) return
      if (scopeRef.current === nextScope) return

      persistTabState(
        storageOwnerRef.current,
        scopeRef.current,
        openTabsRef.current,
        activeTabIdRef.current
      )
      const nextState = getInitialState(nextScope)
      scopeRef.current = nextScope
      setScope(nextScope)
      setOpenTabs(nextState.openTabs)
      setActiveTabId(nextState.activeTabId)
    },
    [siteId, storageOwnerRef]
  )

  const navigateToTab = useCallback(
    (tab: DocumentTab) => {
      navigateToWorkspaceTab(tab, {
        historyPush: (href) => {
          router.history.push(href)
        },
        navigateToDocuments: (options) => router.navigate(options),
      })
    },
    [router]
  )

  const activateWorkspaceTab = useCallback(
    (tabId: string) => {
      const tab = openTabsRef.current.find(
        (candidate) => candidate.id === tabId
      )
      if (!tab) return
      if (tabId !== activeTabIdRef.current && !confirmLeaveActiveTab()) return

      flushSync(() => {
        setActiveTabId(tabId)
      })
      scheduleAfterPaint(() => navigateToTab(tab))
    },
    [confirmLeaveActiveTab, navigateToTab]
  )

  /**
   * Focus an existing tab and retarget its current history entry to a newly
   * requested location (explicit deep links into stable-identity tabs).
   */
  const focusTabAndNavigate = useCallback(
    (tabId: string, item: { label?: string; href: string }) => {
      const tab = openTabsRef.current.find(
        (candidate) => candidate.id === tabId
      )
      if (!tab) return
      if (tabId !== activeTabIdRef.current && !confirmLeaveActiveTab()) return

      const current = tab.history[tab.currentIndex]
      const nextTab: DocumentTab =
        current.type === 'destination'
          ? {
              ...tab,
              history: [
                ...tab.history.slice(0, tab.currentIndex),
                {
                  ...current,
                  href: item.href,
                  label: item.label ?? current.label,
                },
                ...tab.history.slice(tab.currentIndex + 1),
              ],
            }
          : tab

      flushSync(() => {
        setOpenTabs((currentTabs) =>
          currentTabs.map((candidate) =>
            candidate.id === tabId ? nextTab : candidate
          )
        )
        setActiveTabId(tabId)
      })
      scheduleAfterPaint(() => navigateToTab(nextTab))
    },
    [confirmLeaveActiveTab, navigateToTab]
  )

  const openDestinationTab = useCallback(
    (options: OpenDestinationTabOptions) => {
      const isStableIdentity = STABLE_IDENTITY_DESTINATIONS.has(
        options.destinationKey
      )
      const existingIndexTab = findReuseTargetTab(openTabsRef.current, options)

      if (existingIndexTab) {
        if (isStableIdentity && options.reuseMode === 'focus-and-navigate') {
          focusTabAndNavigate(existingIndexTab.id, options)
          return
        }
        activateWorkspaceTab(existingIndexTab.id)
        return
      }

      if (!confirmLeaveActiveTab()) return

      const tab = createTabForDestination(options)
      flushSync(() => {
        setOpenTabs((currentTabs) => [...currentTabs, tab])
        setActiveTabId(tab.id)
      })
      scheduleAfterPaint(() => navigateToTab(tab))
    },
    [
      activateWorkspaceTab,
      confirmLeaveActiveTab,
      focusTabAndNavigate,
      navigateToTab,
    ]
  )

  const openResourceTab = useCallback(
    (options: OpenResourceTabOptions) => {
      const existingTab = openTabsRef.current.find(
        (tab) => tab.resourceKey === options.resourceKey
      )
      if (existingTab) {
        focusTabAndNavigate(existingTab.id, options)
        return
      }

      if (!confirmLeaveActiveTab()) return

      const tab = createResourceTab(options)
      flushSync(() => {
        setOpenTabs((currentTabs) => [...currentTabs, tab])
        setActiveTabId(tab.id)
      })
      scheduleAfterPaint(() => navigateToTab(tab))
    },
    [confirmLeaveActiveTab, focusTabAndNavigate, navigateToTab]
  )

  const openDocumentTab = useCallback(
    (options: OpenDocumentTabOptions) => {
      const resourceKey = documentResourceKey(
        workspaceScopeMode(scopeRef.current),
        options.documentId
      )
      // Prefer the stable key, but a tab whose viewer history drifted to this
      // document (reference jumps) is the same resource — reuse it.
      const existingTab =
        openTabsRef.current.find((tab) => tab.resourceKey === resourceKey) ??
        openTabsRef.current.find((tab) => {
          const item = tab.history[tab.currentIndex]
          return (
            item.type === 'document' && item.documentId === options.documentId
          )
        })

      if (existingTab) {
        if (
          existingTab.id !== activeTabIdRef.current &&
          !confirmLeaveActiveTab()
        ) {
          return
        }
        // Navigate within the tab to the newly requested citation, preserving
        // the viewer's own history so the user can return.
        const current = existingTab.history[existingTab.currentIndex]
        const sameLocation =
          current.type === 'document' &&
          current.documentId === options.documentId &&
          current.pageNumber === (options.pageNumber ?? current.pageNumber) &&
          options.regionHighlight === undefined &&
          options.fragmentHighlight === undefined
        const nextTab: DocumentTab = sameLocation
          ? existingTab
          : {
              ...existingTab,
              history: [
                ...existingTab.history.slice(0, existingTab.currentIndex + 1),
                {
                  type: 'document',
                  documentId: options.documentId,
                  pageNumber:
                    options.pageNumber ??
                    (current.type === 'document' ? current.pageNumber : 1),
                  selectedBoxIds: [],
                  query: '',
                  fragmentHighlight: options.fragmentHighlight,
                  regionHighlight: options.regionHighlight,
                },
              ],
              currentIndex: existingTab.currentIndex + 1,
            }

        flushSync(() => {
          setOpenTabs((currentTabs) =>
            currentTabs.map((candidate) =>
              candidate.id === existingTab.id ? nextTab : candidate
            )
          )
          setActiveTabId(existingTab.id)
        })
        scheduleAfterPaint(() => navigateToTab(nextTab))
        return
      }

      if (!confirmLeaveActiveTab()) return

      const tab = createWorkspaceDocumentTab(options, resourceKey)
      flushSync(() => {
        setOpenTabs((currentTabs) => [...currentTabs, tab])
        setActiveTabId(tab.id)
      })
      scheduleAfterPaint(() => navigateToTab(tab))
    },
    [confirmLeaveActiveTab, navigateToTab]
  )

  const { navigateActiveTab, openNewTab } = useWorkspaceTabNavigation({
    siteId,
    openTabsRef,
    activeTabIdRef,
    scopeRef,
    setOpenTabs,
    setActiveTabId,
    confirmLeaveActiveTab,
    navigateToTab,
    activateWorkspaceTab,
    focusTabAndNavigate,
    openDestinationTab,
    openDocumentTab,
  })

  const openAgenticConversation = useCallback(
    ({
      composerDraft,
      conversation,
      preview,
      siteId,
      title,
    }: OpenAgenticConversationOptions) => {
      const existingTab = conversation.id
        ? openTabsRef.current.find(
            (tab) => getAgenticConversationId(tab) === conversation.id
          )
        : undefined
      if (existingTab) {
        activateWorkspaceTab(existingTab.id)
        return
      }

      if (!confirmLeaveActiveTab()) return

      const tab = createAgenticConversationTab({
        composerDraft,
        conversation,
        preview,
        siteId,
        title,
      })
      flushSync(() => {
        setOpenTabs((currentTabs) => [...currentTabs, tab])
        setActiveTabId(tab.id)
      })
      scheduleAfterPaint(() => navigateToTab(tab))
    },
    [activateWorkspaceTab, confirmLeaveActiveTab, navigateToTab]
  )

  const openNewAgenticChat = useCallback(
    (options?: { question?: string }) => {
      if (!confirmLeaveActiveTab()) return
      const tab = createNewAgenticChatTab({
        siteId,
        question: options?.question,
      })
      flushSync(() => {
        setOpenTabs((currentTabs) => [...currentTabs, tab])
        setActiveTabId(tab.id)
      })
      scheduleAfterPaint(() => navigateToTab(tab))
    },
    [confirmLeaveActiveTab, navigateToTab, siteId]
  )

  const setActiveTabTitle = useCallback((title: string | null) => {
    const nextLabel = title === null ? undefined : title
    setOpenTabs((currentTabs) => {
      let changed = false
      const nextTabs = currentTabs.map((tab) => {
        if (
          tab.id !== activeTabIdRef.current ||
          tab.customLabel === nextLabel
        ) {
          return tab
        }
        changed = true
        return { ...tab, customLabel: nextLabel }
      })
      return changed ? nextTabs : currentTabs
    })
  }, [])

  const setActiveTabDirty = useCallback((dirty: boolean) => {
    const nextDirty = dirty || undefined
    setOpenTabs((currentTabs) => {
      let changed = false
      const nextTabs = currentTabs.map((tab) => {
        if (tab.id !== activeTabIdRef.current || tab.dirty === nextDirty) {
          return tab
        }
        changed = true
        return { ...tab, dirty: nextDirty }
      })
      return changed ? nextTabs : currentTabs
    })
  }, [])

  const closeWorkspaceTab = useCallback(
    (tabId: string) => {
      const currentTabs = openTabsRef.current
      const closingIndex = currentTabs.findIndex((tab) => tab.id === tabId)
      if (closingIndex === -1) return true

      const closingTab = currentTabs[closingIndex]
      if (closingTab?.closable === false) return false
      // Only the active tab's route is mounted, so only it can be dirty.
      if (tabId === activeTabIdRef.current && !confirmLeaveActiveTab()) {
        return false
      }

      clearWorkspaceTabChartState(siteId, tabId)

      if (currentTabs.length === 1) {
        const defaultTab = createWorkspaceHomeTab()
        flushSync(() => {
          setOpenTabs([defaultTab])
          setActiveTabId(defaultTab.id)
        })
        scheduleAfterPaint(() => navigateToTab(defaultTab))
        return true
      }

      const nextTabs = currentTabs.filter((tab) => tab.id !== tabId)
      const shouldActivateNext = activeTabIdRef.current === tabId
      const nextTab = shouldActivateNext
        ? (nextTabs[Math.min(closingIndex, nextTabs.length - 1)] ?? nextTabs[0])
        : undefined

      flushSync(() => {
        setOpenTabs(nextTabs)
        if (nextTab) {
          setActiveTabId(nextTab.id)
        }
      })
      if (nextTab) {
        scheduleAfterPaint(() => navigateToTab(nextTab))
      }
      return true
    },
    [confirmLeaveActiveTab, navigateToTab, siteId]
  )

  // Staged restored-tab validation: persisted tabs render immediately (their
  // routes are independently access-gated, so no protected content can flash),
  // and are removed only once the policy has definitively resolved. A loading
  // policy never deletes tabs.
  const accessPolicyIsLoading = accessPolicy?.isLoading ?? true
  const canOpenTab = accessPolicy?.canOpenTab
  useEffect(() => {
    if (accessPolicyIsLoading || !canOpenTab) return
    // During a site switch this effect can run before the scope-swap effect's
    // state lands; validating then would filter the OLD site's tabs against
    // the NEW site's policy and persist the result into the new site's
    // storage. Wait until the committed scope belongs to this site.
    if (!scope.startsWith(`${siteId}:`)) return

    const allowedTabs = openTabs.filter((tab) => canOpenTab(tab))
    if (allowedTabs.length === openTabs.length) return

    if (allowedTabs.length === 0) {
      const defaultTab = createWorkspaceHomeTab()
      setOpenTabs([defaultTab])
      setActiveTabId(defaultTab.id)
      scheduleAfterPaint(() => navigateToTab(defaultTab))
      return
    }

    const activeRemoved = !allowedTabs.some((tab) => tab.id === activeTabId)
    setOpenTabs(allowedTabs)
    if (activeRemoved) {
      const nextTab = allowedTabs[0]
      setActiveTabId(nextTab.id)
      scheduleAfterPaint(() => navigateToTab(nextTab))
    }
  }, [
    accessPolicyIsLoading,
    activeTabId,
    canOpenTab,
    navigateToTab,
    openTabs,
    scope,
    siteId,
  ])

  const state = useMemo<WorkspaceTabsState>(
    () => ({
      openTabs,
      activeTabId,
    }),
    [activeTabId, openTabs]
  )

  const actions = useMemo<WorkspaceTabsActions>(
    () => ({
      setOpenTabs,
      setActiveTabId,
      setWorkspaceScope,
      openDestinationTab,
      openAgenticConversation,
      openNewAgenticChat,
      openResourceTab,
      openDocumentTab,
      navigateActiveTab,
      openNewTab,
      setActiveTabTitle,
      setActiveTabDirty,
      activateWorkspaceTab,
      closeWorkspaceTab,
      registerNavigationGuard,
      confirmLeaveActiveTab,
    }),
    [
      activateWorkspaceTab,
      closeWorkspaceTab,
      confirmLeaveActiveTab,
      navigateActiveTab,
      openNewTab,
      openDestinationTab,
      openAgenticConversation,
      openDocumentTab,
      openNewAgenticChat,
      openResourceTab,
      registerNavigationGuard,
      setActiveTabTitle,
      setActiveTabDirty,
      setWorkspaceScope,
    ]
  )

  return (
    <WorkspaceTabsActionsContext.Provider value={actions}>
      <WorkspaceTabsStateContext.Provider value={state}>
        {children}
      </WorkspaceTabsStateContext.Provider>
    </WorkspaceTabsActionsContext.Provider>
  )
}

export const useOptionalWorkspaceTabsActions = () =>
  useContext(WorkspaceTabsActionsContext)

export const useOptionalWorkspaceTabsState = () =>
  useContext(WorkspaceTabsStateContext)

/**
 * Hides any surrounding workspace tab state from the subtree. An embedded
 * document viewer hosted INSIDE a workspace page (Create Monitor's viewer,
 * sentinel evidence panels) must own its tabs locally — the workspace's
 * active tab is the hosting page itself, never a document the embed could
 * show — so every dual-path consumer below the barrier takes its legacy
 * standalone branch.
 */
export const WorkspaceTabsBarrier: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <WorkspaceTabsStateContext.Provider value={null}>
    <WorkspaceTabsActionsContext.Provider value={null}>
      {children}
    </WorkspaceTabsActionsContext.Provider>
  </WorkspaceTabsStateContext.Provider>
)

/** Combined optional access for dual-path consumers (e.g. useTabState). */
export const useOptionalWorkspaceTabs =
  (): WorkspaceTabsContextValue | null => {
    const state = useOptionalWorkspaceTabsState()
    const actions = useOptionalWorkspaceTabsActions()
    return useMemo(() => {
      if (!state || !actions) return null
      return { ...state, ...actions }
    }, [actions, state])
  }

export const useWorkspaceTabs = (): WorkspaceTabsContextValue => {
  const context = useOptionalWorkspaceTabs()
  if (!context) {
    throw new Error(
      'useWorkspaceTabs must be used within a WorkspaceTabsProvider'
    )
  }
  return context
}
